import { useEffect, useRef, useState } from 'react';

type UseSessionTimelineScrollDetectionOptions = {
  scrollContainerId?: string;
  thresholdSelector?: string;
};

export const useScrollUpDetection = ({
  scrollContainerId,
  thresholdSelector,
}: UseSessionTimelineScrollDetectionOptions = {}) => {
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const lastScrollTop = useRef(0);
  const hasPassedThreshold = useRef(false);

  useEffect(() => {
    const scrollContainer = scrollContainerId ? document.getElementById(scrollContainerId) : null;
    const target = scrollContainer || window;

    const getScrollTop = () => {
      if (scrollContainer) return scrollContainer.scrollTop;
      return window.scrollY || document.documentElement.scrollTop;
    };

    const handleScroll = () => {
      const currentScrollTop = getScrollTop();
      const isGoingUp = currentScrollTop < lastScrollTop.current;

      if (thresholdSelector) {
        const container = scrollContainer || document;
        const thresholdEl = container.querySelector(thresholdSelector) as HTMLElement | null;

        if (thresholdEl) {
          const elRect = thresholdEl.getBoundingClientRect();
          const containerRect = scrollContainer ? scrollContainer.getBoundingClientRect() : { top: 0 };
          const offsetFromContainer = elRect.top - containerRect.top;

          if (!isGoingUp && offsetFromContainer <= 5) {
            hasPassedThreshold.current = true;
          }

          if (currentScrollTop <= 0) {
            hasPassedThreshold.current = false;
          }
        }
      } else {
        hasPassedThreshold.current = currentScrollTop > 0;
      }

      if (!hasPassedThreshold.current) {
        setIsScrollingUp(false);
      } else {
        setIsScrollingUp(isGoingUp);
      }

      lastScrollTop.current = currentScrollTop;
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [scrollContainerId, thresholdSelector]);

  return isScrollingUp;
};
